import Stripe from 'stripe'
import isObject from 'lodash/isObject'

export type StripeError = Stripe.errors.StripeError

export type StripeAccount = Stripe.Account
export type StripeAccountUpdatedEvent = Stripe.AccountUpdatedEvent
export type StripeAccountLink = Stripe.AccountLink
export type StripePerson = Stripe.Person
export type StripePrice = Stripe.Price
export type StripeExternalAccount = Stripe.ExternalAccount
export type StripeFinancialAccount = Stripe.Treasury.FinancialAccount
export type StripeSetupIntent = Stripe.SetupIntent
export type StripeSetupIntentStatus = Stripe.SetupIntent.Status
export type StripeInboundTransfer = Stripe.Treasury.InboundTransfer
export type StripeOutboundTransfer = Stripe.Treasury.OutboundTransfer
export type StripeOutboundTransferCreateParams = Stripe.Treasury.OutboundTransferCreateParams
export type StripeOutboundPayment = Stripe.Treasury.OutboundPayment
export type StripeOutboundPaymentEvent = Stripe.Event & {
  data: {
    object: Stripe.Treasury.OutboundPayment
  }
}
export type StripeInboundTransferEvent = Stripe.Event & {
  data: {
    object: Stripe.Treasury.InboundTransfer
  }
}
export type StripeOutboundTransferEvent = Stripe.Event & {
  data: {
    object: Stripe.Treasury.OutboundTransfer
  }
}
export type StripeTreasuryReceivedCreditCreatedEvent = Stripe.TreasuryReceivedCreditCreatedEvent
export type StripeTransaction = Stripe.Treasury.Transaction
export type StripeTreasuryFinancialAccountCreatedEvent = Stripe.TreasuryFinancialAccountCreatedEvent
export type StripeTreasuryFinancialAccountFeaturesStatusUpdatedEvent =
  Stripe.TreasuryFinancialAccountFeaturesStatusUpdatedEvent
export type StripeSubscription = Stripe.Subscription
export type StripeSubscriptionStatus = Stripe.Subscription.Status

export const stripeClient = new Stripe(process.env.STRIPE_API_KEY || 'sk_test_1234567890')
export const isStripeError = (error: unknown): error is StripeError => {
  return isObject(error) && 'raw' in error
}

const STRIPE_CUSTOMER_ID_PATTERN = /^cus_[A-Za-z0-9]+$/

export async function searchStripeCustomers(query: string, limit = 10): Promise<Stripe.Customer[]> {
  const escaped = query.replace(/["\\]/g, ' ').trim()
  if (!escaped) {
    return []
  }
  if (STRIPE_CUSTOMER_ID_PATTERN.test(escaped)) {
    try {
      const customer = await stripeClient.customers.retrieve(escaped)
      return customer.deleted ? [] : [customer]
    } catch (error) {
      if (isStripeError(error) && error.code === 'resource_missing') {
        return []
      }
      throw error
    }
  }
  const { data } = await stripeClient.customers.search({
    query: `name~"${escaped}" OR email~"${escaped}"`,
    limit
  })
  return data
}

export async function createBillingPortalSession(customerId: string, returnUrl: string): Promise<string> {
  const session = await stripeClient.billingPortal.sessions.create({ customer: customerId, return_url: returnUrl })
  return session.url
}

export const STRIPE_SETTLED_STATUSES = ['posted', 'succeeded'] as const

export const STRIPE_CANCELED_STATUS = 'cancelled' as const
